import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model"
import type { MenuItemInterface } from "@/models/menu-item"
import type { CartLine } from "@/store/cart/cart.types"
import { memoryCacheSet } from "@/lib/safe-query/memory-cache"
import { buildPricingLiveFingerprint, pricingPreviewQueryKey } from "@/query-keys/pricing-preview.keys"

/**
 * Warms safe-query memory so checkout's first `useSafeQuery` hit avoids a cold fetch flash.
 * Call with the same fingerprint inputs as {@link usePricingPreview}.
 *
 * @returns The fingerprint and query key used (for publishing Pricing Confirmation into the sync owner).
 */
export function primePricingPreviewMemoryCache(input: {
  restaurantId: string
  userName: string
  billing: BillingPreviewUI
  cartLines: CartLine[]
  discountId: string
  menuItems: MenuItemInterface[]
  addressFingerprint: string
  orderType: number
  orderChannel: number
  deliveryModeFingerprint?: string
  scheduledSlotFingerprint?: string
}): { fingerprint: string; queryKey: string } | null {
  const fp = buildPricingLiveFingerprint({
    cartLines: input.cartLines,
    discountId: input.discountId,
    menuItems: input.menuItems,
    addressFingerprint: input.addressFingerprint,
    orderType: input.orderType,
    orderChannel: input.orderChannel,
    deliveryModeFingerprint: input.deliveryModeFingerprint,
    scheduledSlotFingerprint: input.scheduledSlotFingerprint,
  })
  const key = pricingPreviewQueryKey(input.restaurantId, input.userName, fp)
  if (key) {
    memoryCacheSet(key, input.billing)
    return { fingerprint: fp, queryKey: key }
  }
  return null
}
